



















































































import Vue from "vue";
import { Component, Prop, Inject } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { kebabCase } from "lodash";
import {
    ISelectListItem,
    IValidationRules,
    IPropertyValidationRules
} from "../models";
import { AppGetterTypes, GetPropertyValidationRules } from "../store";
import { RestClient } from "@elite/restclient";

// don't inject attributes to the root element - see https://vuejs.org/v2/api/#inheritAttrs
@Component({ inheritAttrs: false })
export default class ValFormInput extends Vue {
    @Inject()
    restClient!: RestClient;

    @Getter(AppGetterTypes.validationRules)
    validationRules!: IValidationRules;

    @Getter(AppGetterTypes.getPropertyValidationRules)
    getPropertyValidationRules!: GetPropertyValidationRules;

    @Prop()
    disabled?: boolean;

    @Prop({ required: true })
    rulesName!: string;

    @Prop({ required: false })
    fieldName!: string | null;

    @Prop({ default: "text" })
    type!: string;

    @Prop({ required: false, default: "" })
    placeholder!: string;

    @Prop({ required: true })
    value!: string;

    @Prop({ default: true })
    displayLabel!: boolean;

    @Prop({ default: false })
    disableTextboxAutocomplete!: boolean;

    @Prop()
    id!: string;

    @Prop()
    options?: ISelectListItem[];
    @Prop()
    optionsUrl?: string;

    ajaxOptions: ISelectListItem[] = [];

    propertyErrors: string[] = [];

    get localValue(): string {
        return this.value;
    }
    set localValue(val: string) {
        this.$emit("input", val);
    }

    get name(): string {
        // get it from the attribute or the property name - kebab case helps browsers with autocomplete
        return this.$attrs.name || kebabCase(this.rules.propertyName);
    }

    get displayName(): string {
        // The rules name should match the path to the locale message that stores the display name
        // ex: addressModel.firstName

        // See locale/languages/IEverbrightLocaleMessages.ts
        return (
            this.fieldName ||
            this.placeholder ||
            this.$t(this.rulesName).toString()
        );
    }

    get autocomplete(): string {
        // do we have an explicit attribute?
        if (this.$attrs.autocomplete) {
            return this.$attrs.autocomplete;
        }

        // special case for disabling
        if (this.disableTextboxAutocomplete) {
            return "new-password";
        }

        // browsers want this to be separate words, e.g. shipping address.
        // if it needs to be more specific, use the autocomplete attribute in the consuming component.
        // E.g. autocomplete="shipping postal-code" or "shipping given-name".
        // see https://cloudfour.com/thinks/autofill-what-web-devs-should-know-but-dont/
        // and https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill
        return this.name.split("-").join(" ");
    }

    get rules(): IPropertyValidationRules {
        return this.getPropertyValidationRules(this.rulesName);
    }

    get localOptions(): ISelectListItem[] {
        return this.options || this.ajaxOptions;
    }

    get vid(): string {
        return this.id || this.rules.propertyName;
    }

    selectListWithPlaceholder(
        items: ISelectListItem[],
        placeholder: string
    ): ISelectListItem[] {
        // b-form-select doesn't support a placeholder, so we add a disabled option
        const placeholderItem: ISelectListItem = {
            text: placeholder,
            // value needs to be "" if autocomplete is set otherwise it won't display
            value: this.$attrs.autocomplete ? "" : null,
            disabled: true
        };

        return [placeholderItem, ...items];
    }

    mounted(): void {
        this.$nextTick(() => {
            this.$root.$on(
                `validate-${this.rules.propertyName}`,
                (errors: string[]) => {
                    this.propertyErrors = errors;
                }
            );
        });

        if (this.type === "select" && this.optionsUrl) {
            this.restClient
                .getJson<ISelectListItem[]>(this.optionsUrl)
                .then(response => {
                    this.ajaxOptions = response.data || [];
                });
        }
    }

    search(search: string, loading: (isLoading: boolean) => void): void {
        this.$emit("search", search, loading);
    }
}
